import type { PipSessionData } from '@/electron/bridge/pip-call';
import { isDesktop } from '@/lib/platform';
import { usePowerDialerContacts } from '@/views/power-dialer/hooks/use-power-dialer-contacts';
import { getSessionContactKey } from '@/views/power-dialer/types';
import { useEffect, useMemo } from 'react';
import { useActivePowerDialer } from './active-power-dialer-provider';

/**
 * Module-level store so the web PiP portal can read session data
 * synchronously (same JS context, no IPC needed).
 */
let currentPipSessionData: PipSessionData | null = null;

/** Read the current session data (called from the web PiP portal). */
export const getPipSessionData = (): PipSessionData | null => currentPipSessionData;

/** Module-level callback for ending the session from the web PiP portal. */
let endSessionCallback: (() => void) | null = null;

/** Callback to resize PiP after session ends. */
let onSessionEndResize: (() => void) | null = null;

/** Register a callback to resize PiP when session ends. */
export const setPipSessionEndResize = (cb: (() => void) | null) => {
  onSessionEndResize = cb;
};

/** End the session from the web PiP portal. */
export const pipEndSession = () => {
  endSessionCallback?.();
  // Resize after a tick so the session data clears first
  setTimeout(() => onSessionEndResize?.(), 50);
};

/**
 * Renderless bridge that sends Power Dialer session data directly to the
 * PiP window via a dedicated IPC channel (`pip-call/updateSessionData`).
 *
 * Must be mounted **inside** `<ActivePowerDialerProvider>`.
 */
export const PowerDialerPipBridge = () => {
  const { activeSessionId, activeContactId, stateOverrides, endSession, state } =
    useActivePowerDialer();
  const { contacts } = usePowerDialerContacts();

  const sessionData: PipSessionData | null = useMemo(() => {
    const isRunningSession = state.phase === 'dialing' || state.phase === 'paused';
    if (!activeSessionId || !isRunningSession) {
      return null;
    }
    const enriched = contacts.map((c) => ({
      id: getSessionContactKey(c),
      name: c.name,
      lastName: c.last_name,
      phoneNumber: c.numbers?.[0],
      jobTitle: c.job_title ?? undefined,
      companyName: c.company?.name,
      dialingState: (stateOverrides[getSessionContactKey(c)] ||
        c.dialing_state) as PipSessionData['contacts'][0]['dialingState'],
      imageUrl: c.image_url ?? undefined,
    }));
    const completed = Object.values(stateOverrides).filter(
      (s) => s === 'connected' || s === 'no-answer' || s === 'voicemail',
    ).length;
    const queueCount = enriched.filter(
      (c) => ['pending', 'ringing'].includes(c.dialingState) || c.id === activeContactId,
    ).length;
    return {
      isActive: true,
      contacts: enriched,
      activeContactId,
      completedCount: completed,
      totalCount: queueCount + completed,
      minutesLeft: Math.max(1, Math.round((queueCount - 1) * 2)),
    };
  }, [activeSessionId, activeContactId, contacts, state.phase, stateOverrides]);

  // Always keep endSession callback available
  useEffect(() => {
    endSessionCallback = endSession;
    return () => {
      endSessionCallback = null;
    };
  }, [endSession]);

  // Write to module store (for web PiP portal) + send via IPC (for Electron PiP)
  useEffect(() => {
    currentPipSessionData = sessionData;
    if (isDesktop()) {
      window.desktopAPI?.pipCall.updateSessionData.callback(sessionData);
    }
    return () => {
      currentPipSessionData = null;
    };
  }, [sessionData]);

  // Make session end authoritative even if the call SDK is still reporting an
  // active transition such as ringing while hangup cleanup catches up.
  useEffect(() => {
    if (!isDesktop() || state.phase !== 'ended') {
      return;
    }
    currentPipSessionData = null;
    window.desktopAPI?.pipCall.destroy.callback();
  }, [state.phase]);

  // Electron: listen for "End session" from PiP window via IPC
  useEffect(() => {
    if (!isDesktop()) {
      return;
    }
    const cleanup = window.desktopAPI?.pipCall.onEndSession.callback(endSession);
    return () => cleanup?.();
  }, [endSession]);

  return null;
};
